import { useState, useEffect } from "react";
import { toast } from "sonner";
import { TrendingUp, Layers, ShieldCheck, FileText, Lock, ArrowRight, ExternalLink, Sparkles, Send } from "lucide-react";
import { Link } from "react-router-dom";
import { TRACTION } from "../lib/site";
import { askDeckStream, fetchPopularQuestions, apiErr } from "../lib/api";
import { trackEvent } from "../lib/analytics";
import { PageHero, SectionHeading, CTASection } from "../components/site/Shared";
import { Reveal, Stagger, StaggerItem } from "../components/site/Reveal";
import { InvestorDialog } from "../components/site/InvestorDialog";

const SUGGESTED = [
  "What problem does PARTECON solve?",
  "What is the funding round and traction?",
  "What is the business model?",
  "Who are the target customers?",
];

const renderWithCitations = (text) => {
  const parts = String(text).split(/(\(Slide\s*\d+\))/g);
  return parts.map((part, i) => {
    const m = part.match(/\(Slide\s*(\d+)\)/);
    if (m) {
      return (
        <a key={i} href={`/partecon-pitch-deck.pdf#page=${m[1]}`} target="_blank" rel="noopener noreferrer"
          data-testid="deck-citation" className="text-[#6366F1] hover:underline whitespace-nowrap">{part}</a>
      );
    }
    return <span key={i}>{part}</span>;
  });
};

const DeckQA = () => {
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState("");
  const [asked, setAsked] = useState("");
  const [popular, setPopular] = useState([]);

  useEffect(() => {
    fetchPopularQuestions().then((r) => setPopular(r.data || [])).catch(() => {});
  }, []);

  const ask = async (question) => {
    const text = (question || q).trim();
    if (!text || loading) return;
    setLoading(true); setAsked(text); setAnswer("");
    trackEvent("deck_qa_ask", { question_length: text.length });
    try {
      await askDeckStream(text, (chunk) => setAnswer((prev) => prev + chunk));
    } catch (err) {
      toast.error(err.message || apiErr(err));
    } finally { setLoading(false); }
  };

  return (
    <section className="py-16 md:py-24 bg-[#0E1117]" data-testid="deck-qa">
      <div className="max-w-4xl mx-auto px-5 sm:px-8">
        <Reveal>
          <SectionHeading center overline="Ask the Deck"
            title="Ask about PARTECON."
            sub="An AI assistant answers using our pitch deck as the source of truth — it won’t invent or alter any figures. If something isn’t in the materials, it will say so." />
        </Reveal>
        <Reveal delay={0.1}>
          <div className="glass rounded-3xl p-6 md:p-8 mt-10">
            <div className="flex gap-3">
              <input
                data-testid="deck-qa-input"
                className="flex-1 bg-white/5 border border-white/10 rounded-full px-5 py-3.5 text-sm text-white placeholder:text-[#9DA9BA]/60 focus:border-[#6366F1]/50 focus:outline-none transition-colors"
                placeholder="Ask about the product, market, traction, business model…"
                value={q}
                onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && ask()}
              />
              <button onClick={() => ask()} disabled={loading} data-testid="deck-qa-send" className="btn-primary px-5 py-3.5 text-sm inline-flex items-center gap-2 disabled:opacity-60">
                {loading ? "Thinking…" : <>Ask <Send size={14} /></>}
              </button>
            </div>
            <div className="flex flex-wrap gap-2 mt-4">
              {SUGGESTED.map((s) => (
                <button key={s} onClick={() => { setQ(s); ask(s); }} data-testid="deck-qa-suggested"
                  className="text-xs text-[#9DA9BA] hover:text-[#6366F1] border border-white/10 hover:border-[#6366F1]/40 rounded-full px-3 py-1.5 transition-colors">
                  {s}
                </button>
              ))}
            </div>
            {(asked || loading) && (
              <div className="mt-7 border-t border-white/10 pt-6" data-testid="deck-qa-answer">
                {asked && <p className="text-sm text-[#A5B4FC] font-mono-accent mb-3">Q: {asked}</p>}
                {loading && !answer ? (
                  <p className="text-[#9DA9BA] text-sm animate-pulse">Reading the deck…</p>
                ) : (
                  <p className="text-white/90 leading-relaxed whitespace-pre-wrap flex gap-3">
                    <Sparkles size={18} className="text-[#6366F1] shrink-0 mt-1" />
                    <span>{renderWithCitations(answer)}{loading && <span className="inline-block w-1.5 h-4 bg-[#6366F1] ml-0.5 align-middle animate-pulse" />}</span>
                  </p>
                )}
              </div>
            )}
            <p className="text-[#9DA9BA]/60 text-xs mt-6 font-mono-accent">Answers are generated from the PARTECON pitch deck. Verify figures against the source document.</p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const highlights = [
  { icon: TrendingUp, t: "Category creation", d: "A new software category — the Governance-First School Operating System — with a defensible, connection-first moat." },
  { icon: Layers, t: "Ecosystem lock-in", d: "Six connected apps and dashboards create deep, multi-stakeholder engagement per school." },
  { icon: FileText, t: "Product & prototype ready", d: "Multi-platform UI/UX and an interactive product prototype completed, with product architecture developed." },
  { icon: ShieldCheck, t: "Recurring model", d: "Native subscription and billing management drives durable, per-school recurring revenue." },
];

export default function Investors() {
  return (
    <>
      <PageHero
        overline="For Investors"
        title="Building a new software category for schools."
        sub="Explore PARTECON’s product, business model, market opportunity, traction and strategic vision through our investor materials."
      />

      <section className="py-16 md:py-20" data-testid="investor-metrics">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Stagger className="grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/10 rounded-2xl overflow-hidden">
            {TRACTION.map((t) => (
              <StaggerItem key={t.label}>
                <div className="bg-[#0E1117] p-8 text-center h-full">
                  <div className="font-heading text-4xl md:text-5xl font-light text-[#6366F1]">{t.value}</div>
                  <p className="text-[#9DA9BA] text-sm mt-3">{t.label}</p>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-16 md:py-24 bg-[#0E1117]">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><SectionHeading overline="The Opportunity" title="Why PARTECON, why now." /></Reveal>
          <Stagger className="grid md:grid-cols-2 gap-4 mt-12">
            {highlights.map((h) => (
              <StaggerItem key={h.t}>
                <div className="glass glass-hover rounded-2xl p-8 h-full flex gap-5" data-testid={`investor-${h.t.split(" ")[0].toLowerCase()}`}>
                  <span className="h-11 w-11 shrink-0 rounded-xl bg-[#6366F1]/10 flex items-center justify-center">
                    <h.icon size={20} className="text-[#6366F1]" />
                  </span>
                  <div>
                    <h3 className="font-heading text-lg font-medium">{h.t}</h3>
                    <p className="text-[#9DA9BA] mt-2 leading-relaxed">{h.d}</p>
                  </div>
                </div>
              </StaggerItem>
            ))}
          </Stagger>
        </div>
      </section>

      <section className="py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal><SectionHeading overline="Investor Resources" title="Explore the opportunity." /></Reveal>
          <Stagger className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
            <StaggerItem>
              <div className="glass glass-hover rounded-3xl p-8 h-full flex flex-col" data-testid="investor-brief">
                <span className="h-11 w-11 rounded-xl bg-[#6366F1]/10 flex items-center justify-center"><Sparkles size={20} className="text-[#6366F1]" /></span>
                <h3 className="font-heading text-xl font-medium mt-5">Investor Brief</h3>
                <p className="text-[#9DA9BA] text-sm mt-3 leading-relaxed flex-1">A concise, shareable one-page brief covering the problem, thesis, product, traction and what's next — public link.</p>
                <Link to="/brief" data-testid="investor-brief-btn" className="btn-primary px-6 py-3 text-sm inline-flex items-center justify-center gap-2 mt-6">Open Brief <ArrowRight size={15} /></Link>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="glass glass-hover rounded-3xl p-8 h-full flex flex-col" data-testid="investor-deck">
                <span className="h-11 w-11 rounded-xl bg-[#6366F1]/10 flex items-center justify-center"><FileText size={20} className="text-[#6366F1]" /></span>
                <h3 className="font-heading text-xl font-medium mt-5">Investor Pitch Deck</h3>
                <p className="text-[#9DA9BA] text-sm mt-3 leading-relaxed flex-1">A concise overview of PARTECON — the product, market opportunity, traction, business model and investment thesis.</p>
                <InvestorDialog title="Access the Pitch Deck" description="Share your name and email to open the PARTECON pitch deck." submitLabel="View Pitch Deck" deckOnSuccess>
                  <button data-testid="investor-deck-btn" className="btn-primary px-6 py-3 text-sm inline-flex items-center justify-center gap-2 mt-6 w-full">View Pitch Deck <ExternalLink size={15} /></button>
                </InvestorDialog>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="glass glass-hover rounded-3xl p-8 h-full flex flex-col" data-testid="investor-plan">
                <span className="h-11 w-11 rounded-xl bg-[#6366F1]/10 flex items-center justify-center"><Lock size={20} className="text-[#6366F1]" /></span>
                <h3 className="font-heading text-xl font-medium mt-5">Business Plan</h3>
                <p className="text-[#9DA9BA] text-sm mt-3 leading-relaxed flex-1">A detailed overview of PARTECON’s business model, development plan, market analysis and execution strategy. Available on request.</p>
                <InvestorDialog title="Request Investor Access" description="Share your details and our team will get in touch with you regarding investor materials and access." submitLabel="Submit Request">
                  <button data-testid="investor-plan-btn" className="btn-ghost px-6 py-3 text-sm inline-flex items-center justify-center gap-2 mt-6 w-full">Request Investor Access <ArrowRight size={15} /></button>
                </InvestorDialog>
              </div>
            </StaggerItem>
            <StaggerItem>
              <div className="glass glass-hover rounded-3xl p-8 h-full flex flex-col" data-testid="investor-product">
                <span className="h-11 w-11 rounded-xl bg-[#6366F1]/10 flex items-center justify-center"><Layers size={20} className="text-[#6366F1]" /></span>
                <h3 className="font-heading text-xl font-medium mt-5">Interactive Product</h3>
                <p className="text-[#9DA9BA] text-sm mt-3 leading-relaxed flex-1">Experience how PARTECON is designed for every stakeholder through interactive product prototypes.</p>
                <Link to="/explore" data-testid="investor-product-btn" className="btn-ghost px-6 py-3 text-sm inline-flex items-center justify-center gap-2 mt-6">Explore Product <ArrowRight size={15} /></Link>
              </div>
            </StaggerItem>
          </Stagger>
        </div>
      </section>

      <DeckQA />

    </>
  );
}
